import React from "react";
import { useHistory } from "react-router-dom";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";

/**
 * Search link for one Play experience type. Filters by the live sub-category ID when the
 * registry has a matching name (it returns e.g. "Event" for "events"), otherwise falls back
 * to the `category` param the previous page used.
 */
export function playSearchUrl(key: string, subcategories: ListingSubcategory[] = []) {
  const match = subcategories.find(sub => {
    const name = String(sub.name || "").toLowerCase();
    return name === key || key.startsWith(name) || name.startsWith(key);
  });
  return match
    ? `/workspaces-search?type=play&categoryId=3&subCategoryId=${match.id}`
    : `/workspaces-search?type=play&category=${key}`;
}

export const PLAY_COLORS: Record<string, string> = {
  wellness: "#017bef",
  dining: "#0062c0",
  adventure: "#0ea5e9",
  events: "#6366f1",
  concierge: "#012d6e",
};

const CARDS = [
  { key: "adventure", label: "Adventure" },
  { key: "dining", label: "Dining" },
  { key: "wellness", label: "Wellness" },
  { key: "events", label: "Events" },
  // Concierge isn't bookable, so this card scrolls to the section below
  { key: "concierge", label: "Concierge", to: "#concierge" },
];

export default function PlayIntro({ subcategories = [] }: { subcategories?: ListingSubcategory[] }) {
  const history = useHistory();

  const open = (to: string) => {
    if (to.startsWith("#")) {
      document.getElementById(to.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else {
      history.push(to);
    }
  };

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-2 lg:gap-10">
          <div>
            <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Experiences</p>
            <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
              More to Do.
              <br />
              More to Discover.
            </h2>
          </div>
          <p className="text-[18px] leading-[29.25px] text-[#717182]">
            SPACIA PLAY helps you make the most of your time away from the desk whether you want to relax, eat somewhere
            memorable, explore somewhere new, attend an event or have someone take care of the details.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-[16px] pt-[34px] sm:grid-cols-3 lg:grid-cols-5">
          {CARDS.map(card => (
            <button
              key={card.key}
              type="button"
              onClick={() => open(card.to ?? playSearchUrl(card.key, subcategories))}
              className="group relative h-[240px] overflow-hidden rounded-2xl text-left sm:h-[346px]"
            >
              <img
                src={`/figma-assets/play/intro-${card.key}.jpg`}
                alt={card.label}
                className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-[20px]">
                <span
                  className="inline-block rounded-full px-2.5 py-[3px] text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white"
                  style={{ backgroundColor: PLAY_COLORS[card.key] }}
                >
                  {card.label}
                </span>
                <h3 className="pt-2 text-[14px] font-bold leading-5 text-white">{card.label}</h3>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
